"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import posthogClient from "posthog-js";
import { useEffect, useState } from "react";
import { DevSignInForm } from "@/components/auth/dev-sign-in-form";
import { OAuthButton } from "@/components/auth/oauth-button";
import { Logo } from "@/components/logo";
import { useAuthCallback } from "@/hooks/use-auth-callback";
import { getEnabledProviders } from "@/lib/oauth";
import { safeNextPath } from "@/lib/safe-next-path";
import { useSession } from "@domainstack/auth/client";
import { Icon } from "@domainstack/ui/icon";
import { cn } from "@domainstack/ui/utils";
interface LoginContentProps {
  /** Additional classes for the wrapper */
  className?: string;
  /** Callback when navigating away (e.g., to close modal) */
  onNavigate?: () => void;
  /** URL to redirect to after successful sign-in (defaults to current page or /dashboard) */
  callbackURL?: string;
}
export function LoginContent(t0) {
  const $ = _c(40);
  const {
    className,
    onNavigate,
    callbackURL
  } = t0;
  const [loadingProvider, setLoadingProvider] = useState(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    data: session,
    isPending
  } = useSession();
  useAuthCallback();
  let t1;
  if ($[0] !== isPending || $[1] !== pathname || $[2] !== session?.user) {
    t1 = () => {
      if (isPending || session?.user) {
        return;
      }
      posthogClient.capture("signup_pageview", {
        pathname
      });
    };
    $[0] = isPending;
    $[1] = pathname;
    $[2] = session?.user;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = session?.user;
  let t3;
  if ($[4] !== isPending || $[5] !== pathname || $[6] !== t2) {
    t3 = [isPending, pathname, t2];
    $[4] = isPending;
    $[5] = pathname;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t1, t3);
  let t4;
  if ($[8] !== searchParams) {
    t4 = safeNextPath(searchParams.get("next"));
    $[8] = searchParams;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const next = t4;
  let t5;
  if ($[10] !== callbackURL || $[11] !== next || $[12] !== pathname || $[13] !== searchParams) {
    t5 = callbackURL ?? next ?? (["/", "/login"].includes(pathname) ? "/dashboard" : pathname + (searchParams.toString() ? `?${searchParams.toString()}` : ""));
    $[10] = callbackURL;
    $[11] = next;
    $[12] = pathname;
    $[13] = searchParams;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const effectiveCallbackURL = t5;
  let t6;
  if ($[15] !== className) {
    t6 = cn("flex flex-col items-center", className);
    $[15] = className;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  let t8;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Icon size="xl" variant="muted" className="mb-5"><Logo /></Icon>;
    t8 = <h1 className="mb-2 text-xl font-semibold tracking-tight">Welcome to Domainstack</h1>;
    t9 = <p className="mb-6 text-center text-sm text-muted-foreground">Sign in to track your domains and receive health alerts.</p>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t10;
  if ($[20] !== effectiveCallbackURL || $[21] !== loadingProvider) {
    t10 = getEnabledProviders().map(provider => <OAuthButton key={provider.id} provider={provider} callbackURL={effectiveCallbackURL} isLoading={loadingProvider === provider.id} isAnyLoading={loadingProvider !== null} onLoadingChange={loading => setLoadingProvider(loading ? provider.id : null)} />);
    $[20] = effectiveCallbackURL;
    $[21] = loadingProvider;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== effectiveCallbackURL || $[24] !== onNavigate) {
    t11 = process.env.NODE_ENV === "development" && <DevSignInForm callbackURL={effectiveCallbackURL} onNavigate={onNavigate} />;
    $[23] = effectiveCallbackURL;
    $[24] = onNavigate;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t10 || $[27] !== t11) {
    t12 = <div className="flex w-full flex-col gap-3">{t10}{t11}</div>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== onNavigate) {
    t13 = <Link href="/terms" onClick={onNavigate} className="underline underline-offset-2 hover:text-foreground">Terms of Service</Link>;
    $[29] = onNavigate;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== onNavigate) {
    t14 = <Link href="/privacy" onClick={onNavigate} className="underline underline-offset-2 hover:text-foreground">Privacy Policy</Link>;
    $[31] = onNavigate;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t13 || $[34] !== t14) {
    t15 = <p className="mt-6 text-center text-xs leading-relaxed text-muted-foreground">By signing in, you agree to our{" "}{t13}{" "}and{" "}{t14}.</p>;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t12 || $[37] !== t15 || $[38] !== t6) {
    t16 = <div className={t6}>{t7}{t8}{t9}{t12}{t15}</div>;
    $[36] = t12;
    $[37] = t15;
    $[38] = t6;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
}